import { registerPlugin } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';

import { isNative } from './host';

/**
 * Fetching a new build and handing it to Android's installer.
 *
 * A link was the first attempt and it does not work: inside the application a
 * tap on one leaves for the browser and lands on the site, and whether anything
 * downloads after that is between the person and whichever browser opened. So
 * the application fetches the file itself, reports how far it has got, and asks
 * Android to install it.
 *
 * "Asks" is the whole of it. What appears is Android's own installer, with its
 * own warning about where the package came from, and only after the person has
 * allowed this application to install packages at all. Nothing here is quiet.
 */

interface InstallerPlugin {
  install(options: { path: string }): Promise<void>;
}

const Installer = registerPlugin<InstallerPlugin>('Installer');

/** Where the download lands. External storage on purpose: the installer is
 *  another application, and it has to be able to read what it is given. */
const DIRECTORY = Directory.External;
const FILE = 'storageapp-update.apk';

export type InstallFailure =
  | 'download_failed'
  | 'not_downloaded'
  | 'no_installer'
  | 'path_not_shareable'
  | 'unknown';

/**
 * Download the build at `url` and open Android's installer on it.
 *
 * `onProgress` is called with a fraction from zero to one where the server said
 * how big the file is, which it does. Fourteen megabytes over a workshop's
 * connection is long enough that a button with nothing happening reads as a
 * button that did not work.
 */
export async function installUpdate(
  url: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  if (!isNative) throw new Error('unknown');

  const listener = onProgress
    ? await Filesystem.addListener('progress', (event) => {
        if (event.contentLength > 0) onProgress(event.bytes / event.contentLength);
      })
    : null;

  try {
    // Overwritten rather than kept: one update at a time, and a phone is not
    // the place to leave fourteen megabytes of a version already installed.
    const downloaded = await Filesystem.downloadFile({
      url,
      path: FILE,
      directory: DIRECTORY,
      progress: true,
    });

    const path = downloaded.path;
    if (!path) throw new Error('download_failed');

    await Installer.install({ path });
  } catch (error) {
    const code = (error as { message?: string })?.message ?? '';
    // The plugin rejects with its own words; anything else came from the
    // download and reads the same to somebody standing there.
    throw new Error(
      ['not_downloaded', 'no_installer', 'path_not_shareable'].includes(code)
        ? code
        : 'download_failed',
    );
  } finally {
    await listener?.remove();
  }
}
